import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Sidebar } from "primereact/sidebar";
import { toast } from "react-toastify";

import {
  TbCamera,
  TbEdit,
  TbLock,
  TbMail,
  TbShieldCheck,
  TbUser,
  TbCalendar,
  TbClock,
  TbCheck,
  TbRefresh,
  TbDeviceDesktop,
  TbSparkles,
} from "react-icons/tb";

import api from "../api/axios";
import { useAuth } from "../context/AuthContext";

import "../assets/styles/page.css";
// import "../assets/styles/profileDash.css";

const PAGE_NAME = "Home";

const ADMIN_ANIMATION =
  "https://media.giphy.com/media/qgQUggAC3Pfv687qPC/giphy.gif";

const emptyProfileForm = {
  name: "",
  email: "",
};

const emptyPasswordForm = {
  newPassword: "",
  confirmPassword: "",
};

const getPermissionHeaders = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const showRequestError = (error, fallbackMessage) => {
  if (error.response?.status === 403) return;

  toast.error(
    error.response?.data?.message ||
      fallbackMessage
  );
};

const normaliseUser = (data = {}) => ({
  user_id: data.user_id || data.id || null,
  name: data.name || "",
  email: data.email || "",
  image_url: data.image_url || "",
  role: data.role || "admin",
  role_id: data.role_id || null,
  role_details: data.role_details || null,
  status: data.status || "active",
  createdAt: data.createdAt || null,
  updatedAt: data.updatedAt || null,
  last_login_at:
    data.last_login_at ||
    data.lastLoginAt ||
    null,
  is_super_admin: Boolean(
    data.is_super_admin
  ),
});

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleDateString(
    "en-IN",
    {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }
  );
};

const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString(
    "en-IN",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  );
};

const getGreeting = (date) => {
  const hour = date.getHours();

  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  if (hour < 21) return "Good evening";

  return "Good night";
};

const getRoleName = (admin) =>
  admin?.role_details?.role_name ||
  (admin?.is_super_admin
    ? "Super Admin"
    : "Administrator");

const getInitial = (name) =>
  String(name || "A")
    .trim()
    .charAt(0)
    .toUpperCase();

/*
 * Keep the one-second clock update inside its own component.
 * This prevents the complete profile dashboard from rerendering
 * every second and looking like it is continuously reloading.
 */
const ProfileLiveClock = () => {
  const [currentTime, setCurrentTime] =
    useState(() => new Date());

  useEffect(() => {
    const intervalId = window.setInterval(
      () => setCurrentTime(new Date()),
      1000
    );

    return () =>
      window.clearInterval(intervalId);
  }, []);

  return (
    <div className="profile-live-clock-card">
      <div className="profile-clock-icon">
        <TbClock />
      </div>

      <span>Local Time</span>

      <strong>
        {currentTime.toLocaleTimeString(
          "en-IN",
          {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          }
        )}
      </strong>

      <p>
        {currentTime.toLocaleDateString(
          "en-IN",
          {
            weekday: "long",
            day: "2-digit",
            month: "long",
            year: "numeric",
          }
        )}
      </p>
    </div>
  );
};

const ProfileDashSkeleton = () => (
  <section className="admin-page profile-dash-page">
    <div className="profile-dashboard-skeleton">
      <div className="profile-skeleton-hero">
        <div>
          <span />
          <h1 />
          <p />
        </div>

        <div className="profile-skeleton-clock" />
      </div>

      <div className="profile-skeleton-grid">
        <div className="profile-skeleton-card large">
          <div className="profile-skeleton-avatar" />
          <span />
          <span />
          <span />
        </div>

        <div className="profile-skeleton-card">
          <span />
          <strong />
          <span />
        </div>

        <div className="profile-skeleton-card">
          <span />
          <strong />
          <span />
        </div>
      </div>

      <div className="profile-skeleton-actions">
        <span />
        <span />
        <span />
      </div>
    </div>
  </section>
);

const ProfileDash = () => {
  const { user: authUser } = useAuth();

  const imageInputRef = useRef(null);
  const imagePreviewRef = useRef("");
  const fetchedAdminIdRef = useRef(null);

  /*
   * Some AuthContext implementations return a new user object on
   * every render. Keep the latest value in a ref so it does not
   * become a profile-fetch effect dependency.
   */
  const sessionUser =
    authUser?.user || authUser || {};

  const sessionUserRef = useRef(sessionUser);
  sessionUserRef.current = sessionUser;

  const authenticatedAdminId =
    sessionUser.user_id || sessionUser.id || null;

  const adminApiPath = authenticatedAdminId
    ? `/admin/users/admins/${authenticatedAdminId}`
    : "";

  const [admin, setAdmin] = useState(() =>
    normaliseUser(sessionUser)
  );

  const [loading, setLoading] =
    useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [savingProfile, setSavingProfile] =
    useState(false);

  const [uploadingImage, setUploadingImage] =
    useState(false);

  const [
    changingPassword,
    setChangingPassword,
  ] = useState(false);

  const [
    editSidebarOpen,
    setEditSidebarOpen,
  ] = useState(false);

  const [
    passwordSidebarOpen,
    setPasswordSidebarOpen,
  ] = useState(false);

  const [profileForm, setProfileForm] =
    useState(emptyProfileForm);

  const [passwordForm, setPasswordForm] =
    useState(emptyPasswordForm);

  const [imagePreview, setImagePreview] =
    useState("");

  const [selectedImage, setSelectedImage] =
    useState(null);

  const greeting = getGreeting(new Date());

  const roleName = useMemo(
    () => getRoleName(admin),
    [admin]
  );

  const profileCompletion = useMemo(() => {
    const values = [
      admin.name,
      admin.email,
      admin.image_url,
      admin.role_details?.role_name ||
        admin.role,
    ];

    return Math.round(
      (values.filter(Boolean).length /
        values.length) *
        100
    );
  }, [admin]);

  const replaceImagePreview = useCallback((
    nextPreview
  ) => {
    const currentPreview =
      imagePreviewRef.current;

    if (
      currentPreview &&
      currentPreview.startsWith("blob:")
    ) {
      URL.revokeObjectURL(
        currentPreview
      );
    }

    imagePreviewRef.current =
      nextPreview || "";

    setImagePreview(
      nextPreview || ""
    );
  }, []);

  const fetchMyProfile = useCallback(async ({
    silent = false,
  } = {}) => {
    const sessionSnapshot =
      sessionUserRef.current || {};

    if (!adminApiPath) {
      const fallback =
        normaliseUser(sessionSnapshot);

      setAdmin(fallback);
      replaceImagePreview(fallback.image_url || "");
      setLoading(false);
      setRefreshing(false);
      return;
    }

    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const response = await api.get(
        adminApiPath,
        {
          headers:
            getPermissionHeaders("view"),
        }
      );

      const returnedProfile =
        response.data?.data ||
        response.data?.user ||
        response.data ||
        {};

      const profile = normaliseUser({
        ...sessionSnapshot,
        ...returnedProfile,
      });

      setAdmin(profile);

      replaceImagePreview(
        profile.image_url || ""
      );
    } catch (error) {
      if (Object.keys(sessionSnapshot).length) {
        const fallback =
          normaliseUser(sessionSnapshot);

        setAdmin(fallback);

        replaceImagePreview(
          fallback.image_url || ""
        );
      }

      showRequestError(
        error,
        "Failed to load your admin profile."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [
    adminApiPath,
    replaceImagePreview,
  ]);

  useEffect(() => {
    if (!authenticatedAdminId) {
      const fallback = normaliseUser(
        sessionUserRef.current || {}
      );

      setAdmin(fallback);
      replaceImagePreview(
        fallback.image_url || ""
      );
      setLoading(false);
      setRefreshing(false);
      return;
    }

    const adminIdKey = String(
      authenticatedAdminId
    );

    /*
     * Fetch once when the authenticated admin ID becomes available.
     * This also avoids duplicate development-mode effect calls and
     * loops caused by unstable AuthContext object references.
     */
    if (
      fetchedAdminIdRef.current ===
      adminIdKey
    ) {
      setLoading(false);
      return;
    }

    fetchedAdminIdRef.current =
      adminIdKey;

    fetchMyProfile();
  }, [
    authenticatedAdminId,
    fetchMyProfile,
    replaceImagePreview,
  ]);

  useEffect(() => {
    return () => {
      const currentPreview =
        imagePreviewRef.current;

      if (
        currentPreview &&
        currentPreview.startsWith("blob:")
      ) {
        URL.revokeObjectURL(
          currentPreview
        );
      }
    };
  }, []);

  const openEditProfile = () => {
    setProfileForm({
      name: admin.name || "",
      email: admin.email || "",
    });

    setSelectedImage(null);

    replaceImagePreview(
      admin.image_url || ""
    );

    setEditSidebarOpen(true);
  };

  const closeEditProfile = () => {
    setEditSidebarOpen(false);
    setSelectedImage(null);

    replaceImagePreview(
      admin.image_url || ""
    );

    setProfileForm(
      emptyProfileForm
    );
  };

  const openPasswordSidebar = () => {
    setPasswordForm(
      emptyPasswordForm
    );

    setPasswordSidebarOpen(true);
  };

  const handleImageSelection = (
    event
  ) => {
    const file =
      event.target.files?.[0] ||
      null;

    event.target.value = "";

    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/webp",
    ];

    if (
      !allowedTypes.includes(file.type)
    ) {
      toast.error(
        "Please select a JPG, PNG or WEBP image."
      );
      return;
    }

    if (
      file.size >
      5 * 1024 * 1024
    ) {
      toast.error(
        "Profile image must be smaller than 5 MB."
      );
      return;
    }

    setSelectedImage(file);

    replaceImagePreview(
      URL.createObjectURL(file)
    );
  };

  const uploadProfileImage = async () => {
    if (!adminApiPath) {
      toast.error(
        "Admin ID was not found in the authenticated session."
      );

      return {
        success: false,
        imageUrl: "",
      };
    }

    if (!selectedImage) {
      return {
        success: true,
        imageUrl: admin.image_url,
      };
    }

    try {
      setUploadingImage(true);

      const formData =
        new FormData();

      formData.append(
        "image",
        selectedImage
      );

      const response = await api.patch(
        `${adminApiPath}/profile-image`,
        formData,
        {
          headers:
            getPermissionHeaders("edit"),
        }
      );

      const updatedImage =
        response.data?.data?.image_url ||
        response.data?.image_url ||
        "";

      if (updatedImage) {
        setAdmin((previous) => ({
          ...previous,
          image_url: updatedImage,
        }));

        replaceImagePreview(
          updatedImage
        );
      }

      setSelectedImage(null);

      return {
        success: true,
        imageUrl: updatedImage,
      };
    } catch (error) {
      showRequestError(
        error,
        "Profile image upload failed."
      );

      return {
        success: false,
        imageUrl: "",
      };
    } finally {
      setUploadingImage(false);
    }
  };

  const submitProfile = async (
    event
  ) => {
    event.preventDefault();

    if (!adminApiPath) {
      toast.error(
        "Admin ID was not found in the authenticated session."
      );
      return;
    }

    const name =
      profileForm.name.trim();

    const email =
      profileForm.email.trim();

    if (!name || !email) {
      toast.error(
        "Name and email are required."
      );
      return;
    }

    try {
      setSavingProfile(true);

      const response = await api.put(
        adminApiPath,
        {
          name,
          email,
        },
        {
          headers:
            getPermissionHeaders("edit"),
        }
      );

      const returnedProfile =
        response.data?.data ||
        response.data?.user ||
        {};

      setAdmin((previous) =>
        normaliseUser({
          ...previous,
          ...returnedProfile,
          name,
          email,
        })
      );

      const uploadResult =
        await uploadProfileImage();

      if (!uploadResult.success) {
        return;
      }

      toast.success(
        "Your profile was updated successfully."
      );

      setEditSidebarOpen(false);
      setProfileForm(
        emptyProfileForm
      );

      await fetchMyProfile({
        silent: true,
      });
    } catch (error) {
      showRequestError(
        error,
        "Profile update failed."
      );
    } finally {
      setSavingProfile(false);
    }
  };

  const submitPasswordChange = async (
    event
  ) => {
    event.preventDefault();

    if (!adminApiPath) {
      toast.error(
        "Admin ID was not found in the authenticated session."
      );
      return;
    }

    const {
      newPassword,
      confirmPassword,
    } = passwordForm;

    if (
      !newPassword ||
      newPassword.length < 6
    ) {
      toast.error(
        "New password must be at least 6 characters."
      );
      return;
    }

    if (
      newPassword !==
      confirmPassword
    ) {
      toast.error(
        "New passwords do not match."
      );
      return;
    }


    try {
      setChangingPassword(true);

      await api.patch(
        `${adminApiPath}/password`,
        { password: newPassword },
        {
          headers:
            getPermissionHeaders("edit"),
        }
      );

      toast.success(
        "Your password was updated successfully."
      );

      setPasswordSidebarOpen(false);

      setPasswordForm(
        emptyPasswordForm
      );
    } catch (error) {
      showRequestError(
        error,
        "Password update failed."
      );
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return <ProfileDashSkeleton />;
  }

  return (
    <section className="admin-page profile-dash-page">
      <div className="profile-dash-hero">
        <div className="profile-dash-hero-copy">
          <div className="profile-dash-kicker">
            <TbSparkles />
            <span>Admin Workspace</span>
          </div>

          <h1>
            {greeting},{" "}
            <span>
              {admin.name || "Admin"}
            </span>
          </h1>

          <p>
            Welcome back to your Wedding
            Stationery
            workspace.
          </p>

          <div className="profile-hero-actions">
            <button
              type="button"
              className="primary-action"
              onClick={openEditProfile}
              disabled={!adminApiPath}
            >
              <TbEdit />
              Edit Profile
            </button>

            <button
              type="button"
              className="ghost-action bottonborder"
              onClick={
                openPasswordSidebar
              }
              disabled={!adminApiPath}
            >
              <TbLock />
              Change Password
            </button>
          </div>
        </div>

        <ProfileLiveClock />

        <div className="profile-hero-decoration profile-decoration-one" />
        <div className="profile-hero-decoration profile-decoration-two" />
      </div>

      <div className="profile-dashboard-grid">
        <article className="profile-main-card page-card">
          <div className="profile-main-card-top">
            <div className="profile-avatar-shell">
              {admin.image_url ? (
                <img
                  src={admin.image_url}
                  alt={admin.name}
                />
              ) : (
                <div className="profile-avatar-fallback">
                  {getInitial(
                    admin.name
                  )}
                </div>
              )}

              <button
                type="button"
                className="profile-camera-button"
                onClick={
                  openEditProfile
                }
                aria-label="Change profile image"
                disabled={!adminApiPath}
              >
                <TbCamera />
              </button>
            </div>

            <div className="profile-main-identity">
              <span
                className={`profile-account-status ${admin.status}`}
              >
                <i />
                {admin.status}
              </span>

              <h2>{admin.name}</h2>
              <p>{admin.email}</p>

              <div className="profile-role-pill">
                <TbShieldCheck />
                {roleName}
              </div>
            </div>

            <button
              type="button"
              className="profile-refresh-button"
              disabled={refreshing || !adminApiPath}
              onClick={() =>
                fetchMyProfile({
                  silent: true,
                })
              }
              aria-label="Refresh profile"
            >
              <TbRefresh
                className={
                  refreshing
                    ? "profile-refreshing"
                    : ""
                }
              />
            </button>
          </div>

          <div className="profile-details-grid">
            {/* <div>
              <span>
                <TbUser />
                Admin ID
              </span>

              <strong>
                #{admin.user_id || "-"}
              </strong>
            </div> */}

            <div>
              <span>
                <TbMail />
                Email Address
              </span>

              <strong>
                {admin.email || "-"}
              </strong>
            </div>

            <div>
              <span>
                <TbShieldCheck />
                Assigned Role
              </span>

              <strong>
                {roleName}
              </strong>
            </div>

            <div>
              <span>
                <TbCalendar />
                Member Since
              </span>

              <strong>
                {formatDate(
                  admin.createdAt
                )}
              </strong>
            </div>
          </div>

          <div className="profile-completion-section">
            <div className="profile-completion-heading">
              <div>
                <span>
                  Profile completion
                </span>

                <small>
                  Complete your admin
                  information and profile
                  image.
                </small>
              </div>

              <strong>
                {profileCompletion}%
              </strong>
            </div>

            <div className="profile-progress-track">
              <span
                style={{
                  width: `${profileCompletion}%`,
                }}
              />
            </div>
          </div>
        </article>

        <article className="profile-animation-card page-card">
          <div className="profile-animation-copy">
            <span>
              Today&apos;s workspace
            </span>

            <h3>
              Keep creating remarkable
              experiences.
            </h3>

            <p>
              Manage products, orders,
              customers and every platform
              detail from one place.
            </p>
          </div>

          <div className="profile-animation-image">
            <img
              src={ADMIN_ANIMATION}
              alt="Animated admin workspace"
              loading="lazy"
            />
          </div>
        </article>

        <article className="profile-stat-card page-card">
          <div className="profile-stat-icon">
            <TbShieldCheck />
          </div>

          <div>
            <span>Account Role</span>
            <strong>{roleName}</strong>
            <small>
              {admin.is_super_admin
                ? "Full platform access"
                : "Role-based access"}
            </small>
          </div>
        </article>

        <article className="profile-stat-card page-card">
          <div className="profile-stat-icon">
            <TbCheck />
          </div>

          <div>
            <span>Account Status</span>
            <strong>
              {String(
                admin.status || "active"
              )
                .charAt(0)
                .toUpperCase() +
                String(
                  admin.status || "active"
                ).slice(1)}
            </strong>

            <small>
              Your account is available
              for administration.
            </small>
          </div>
        </article>

        <article className="profile-stat-card page-card">
          <div className="profile-stat-icon">
            <TbDeviceDesktop />
          </div>

          <div>
            <span>Last Login</span>

            <strong>
              {admin.last_login_at
                ? formatDateTime(
                    admin.last_login_at
                  )
                : "Current session"}
            </strong>

            <small>
              Your current administrative
              session.
            </small>
          </div>
        </article>

        <article className="profile-quote-card page-card">
          <TbSparkles />

          <blockquote>
            “Thoughtful details create
            unforgettable experiences.”
          </blockquote>

          <span>
            Wedding Stationery Admin
          </span>
        </article>
      </div>

      <div className="profile-quick-actions page-card">
        <div>
          <span>Account controls</span>
          <h3>Quick Actions</h3>
        </div>

        <div className="profile-quick-action-buttons">
          <button
            type="button"
            onClick={openEditProfile}
          >
            <TbEdit />

            <span>
              <strong>
                Edit Profile
              </strong>

              <small>
                Change your name or email
              </small>
            </span>
          </button>

          <button
            type="button"
            onClick={() => {
              openEditProfile();

              window.setTimeout(() => {
                imageInputRef.current?.click();
              }, 250);
            }}
          >
            <TbCamera />

            <span>
              <strong>
                Change Image
              </strong>

              <small>
                Upload a new profile image
              </small>
            </span>
          </button>

          <button
            type="button"
            onClick={
              openPasswordSidebar
            }
          >
            <TbLock />

            <span>
              <strong>
                Reset Password
              </strong>

              <small>
                Secure your admin account
              </small>
            </span>
          </button>
        </div>
      </div>

      <Sidebar
        visible={editSidebarOpen}
        position="right"
        onHide={closeEditProfile}
        className="premium-user-sidebar profile-edit-sidebar"
      >
        <div className="sidebar-form-shell">
          <div className="sidebar-form-header">
            <p>My Account</p>
            <h2>Edit Profile</h2>

            <span>
              Update your own account
              information and profile image.
            </span>
          </div>

          <form
            className="premium-sidebar-form"
            onSubmit={submitProfile}
          >
            <div className="profile-edit-image-section">
              <div className="profile-edit-preview">
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Profile preview"
                  />
                ) : (
                  <div>
                    {getInitial(
                      profileForm.name ||
                        admin.name
                    )}
                  </div>
                )}
              </div>

              <button
                type="button"
                className="ghost-action bottonborder"
                onClick={() =>
                  imageInputRef.current?.click()
                }
              >
                <TbCamera />
                Select Image
              </button>

              <input
                ref={imageInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                hidden
                onChange={
                  handleImageSelection
                }
              />

              <small>
                JPG, PNG or WEBP. Maximum
                size: 5 MB.
              </small>
            </div>

            <label>
              Full Name

              <input
                type="text"
                value={profileForm.name}
                onChange={(event) =>
                  setProfileForm(
                    (previous) => ({
                      ...previous,
                      name:
                        event.target.value,
                    })
                  )
                }
                placeholder="Your name"
                required
              />
            </label>

            <label>
              Email Address

              <input
                type="email"
                value={
                  profileForm.email
                }
                onChange={(event) =>
                  setProfileForm(
                    (previous) => ({
                      ...previous,
                      email:
                        event.target.value,
                    })
                  )
                }
                placeholder="admin@example.com"
                required
              />
            </label>

            <div className="profile-readonly-field">
              <span>Assigned Role</span>

              <strong>
                <TbShieldCheck />
                {roleName}
              </strong>

              <small>
                Your role can only be changed
                by an authorised
                administrator.
              </small>
            </div>

            <div className="sidebar-form-footer">
              <button
                type="button"
                className="ghost-action bottonborder"
                onClick={
                  closeEditProfile
                }
                disabled={
                  savingProfile ||
                  uploadingImage
                }
              >
                Cancel
              </button>

              <button
                type="submit"
                className="primary-action"
                disabled={
                  savingProfile ||
                  uploadingImage
                }
              >
                {savingProfile ||
                uploadingImage
                  ? "Saving..."
                  : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      </Sidebar>

      <Sidebar
        visible={
          passwordSidebarOpen
        }
        position="right"
        onHide={() => {
          setPasswordSidebarOpen(false);

          setPasswordForm(
            emptyPasswordForm
          );
        }}
        className="premium-user-sidebar compact-password-sidebar"
      >
        <div className="sidebar-form-shell">
          <div className="sidebar-form-header">
            <p>Account Security</p>
            <h2>Reset Password</h2>

            <span>
              Create a new password for your
              administrator account.
            </span>
          </div>

          <form
            className="premium-sidebar-form"
            onSubmit={
              submitPasswordChange
            }
          >
            <label>
              New Password

              <input
                type="password"
                autoComplete="new-password"
                value={
                  passwordForm.newPassword
                }
                onChange={(event) =>
                  setPasswordForm(
                    (previous) => ({
                      ...previous,
                      newPassword:
                        event.target.value,
                    })
                  )
                }
                placeholder="Minimum 6 characters"
                minLength={6}
                required
              />
            </label>

            <label>
              Confirm New Password

              <input
                type="password"
                autoComplete="new-password"
                value={
                  passwordForm.confirmPassword
                }
                onChange={(event) =>
                  setPasswordForm(
                    (previous) => ({
                      ...previous,
                      confirmPassword:
                        event.target.value,
                    })
                  )
                }
                placeholder="Re-enter new password"
                minLength={6}
                required
              />
            </label>

            <div className="profile-password-note">
              <TbLock />

              <div>
                <strong>
                  Password security
                </strong>

                <p>
                  Use a unique password that
                  you do not use on another
                  website.
                </p>
              </div>
            </div>

            <div className="sidebar-form-footer">
              <button
                type="button"
                className="ghost-action bottonborder"
                onClick={() => {
                  setPasswordSidebarOpen(
                    false
                  );

                  setPasswordForm(
                    emptyPasswordForm
                  );
                }}
                disabled={
                  changingPassword
                }
              >
                Cancel
              </button>

              <button
                type="submit"
                className="primary-action"
                disabled={
                  changingPassword
                }
              >
                {changingPassword
                  ? "Updating..."
                  : "Update Password"}
              </button>
            </div>
          </form>
        </div>
      </Sidebar>
    </section>
  );
};

export default ProfileDash;